import { RevealWrapper } from '@/hooks/useRevealAnimation'

export default function VisionSection() {
  return (
    <section id="vision" className="py-20 md:py-28 bg-background-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="relative bg-primary-700 rounded-2xl md:rounded-3xl overflow-hidden">
          {/* Decorative organic shape */}
          <div className="absolute top-0 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full bg-primary-600/40 blur-3xl pointer-events-none -translate-x-1/2 -translate-y-1/2" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Image */}
            <RevealWrapper>
              <div className="reveal relative h-64 md:h-80 lg:h-auto lg:min-h-[480px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20hopeful%20pathway%20through%20soft%20green%20garden%20with%20morning%20sunlight%20filtering%20through%20trees%2C%20ivory%20and%20sage%20tones%2C%20new%20beginnings%20and%20growth%20symbolism%2C%20peaceful%20natural%20setting%2C%20soft%20focus%2C%20editorial%20nature%20photography&width=800&height=700&seq=daon-vision-image&orientation=squarish"
                  alt="비전 이미지"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-primary-700/40 lg:block hidden" />
              </div>
            </RevealWrapper>

            {/* Text Content */}
            <div className="flex flex-col justify-center p-8 md:p-12 lg:p-14">
              <RevealWrapper>
                <div className="reveal">
                  {/* Decorative quote */}
                  <div className="text-5xl md:text-6xl text-accent-400/40 font-serif leading-none mb-4">
                    &ldquo;
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-background-50 leading-snug mb-6">
                    모두가 마음의 안정을 찾고<br />
                    <span className="text-accent-300">성장의 기쁨</span>을 누리는 세상
                  </h2>
                  <p className="text-background-300/90 leading-relaxed mb-4 text-sm md:text-base">
                    다온심리상담센터는 회복과 성장의 가치를 믿습니다. 어려움 속에서도
                    마음의 힘을 발견하고, 따뜻한 공감 속에서 새로운 시작을 만들어갈 수
                    있도록 돕는 것이 우리의 방향입니다.
                  </p>
                  <p className="text-background-300/80 leading-relaxed mb-8 text-sm md:text-base">
                    개인의 변화가 공동체의 변화로 이어지고, 건강한 마음 문화가
                    지역사회 전체를 따뜻하게 만드는 세상을 꿈꿉니다.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <span className="px-4 py-2 rounded-full bg-accent-500/20 text-accent-300 text-xs md:text-sm font-medium border border-accent-500/30 whitespace-nowrap">
                      회복
                    </span>
                    <span className="px-4 py-2 rounded-full bg-accent-500/20 text-accent-300 text-xs md:text-sm font-medium border border-accent-500/30 whitespace-nowrap">
                      성장
                    </span>
                    <span className="px-4 py-2 rounded-full bg-accent-500/20 text-accent-300 text-xs md:text-sm font-medium border border-accent-500/30 whitespace-nowrap">
                      공감
                    </span>
                    <span className="px-4 py-2 rounded-full bg-accent-500/20 text-accent-300 text-xs md:text-sm font-medium border border-accent-500/30 whitespace-nowrap">
                      행복
                    </span>
                  </div>
                </div>
              </RevealWrapper>
            </div>
          </div>
        </div>

        {/* Bottom vision cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-8 md:mt-10">
          <RevealWrapper>
            <div className="reveal bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-seedling-line text-xl text-primary-600" />
                </div>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-primary-700 mb-2">새로운 시작</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                매 순간이 새로운 가능성이 될 수 있도록 마음의 문을 열어드립니다.
              </p>
            </div>
          </RevealWrapper>
          <RevealWrapper>
            <div className="reveal reveal-delay-1 bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-heart-3-line text-xl text-secondary-600" />
                </div>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-primary-700 mb-2">마음의 쉼</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                정서적 안정을 위한 따뜻하고 안전한 공간을 제공합니다.
              </p>
            </div>
          </RevealWrapper>
          <RevealWrapper>
            <div className="reveal reveal-delay-2 bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-group-line text-xl text-accent-600" />
                </div>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-primary-700 mb-2">함께하는 성장</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                공동체 속에서 서로를 지지하며 건강한 마음 문화를 만들어갑니다.
              </p>
            </div>
          </RevealWrapper>
        </div>
      </div>
    </section>
  )
}